import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Card, CardContent, CircularProgress, Container, Divider, Modal, Typography, Select, MenuItem, makeStyles, Checkbox, Grid } from '@material-ui/core';
import { Button } from '@fifth-gear-tech/common-components/dist/components';
import { ButtonType, Colors } from '@fifth-gear-tech/common-components/dist/utils/StyledTheme';

import JobSheet from '../JobSheet';
import ColorBandSelector from '../ColorBandSelector';
import { CACHE_KEY } from '../../constants';

const DEFAULT_OPTIONS = { drawings: true, layouts: true, parts: true, nonParts: true };

const useStyles = makeStyles({
    card: { marginTop: '1%', width: '50%', '&:focus': { outline: 'none', boxShadow: 'none' } },
    modalHeader: { marginBottom: '0.5rem', fontWeight: '500' },
    divider: { margin: '1rem' },
    openButton: { width: 'calc(100% - 2rem)' }
});

const generate = (innerHtml) => `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Print Preview</title>
            <style>
                /* page size */
                @page {
                    margin: 0.25in;
                    width: 8.5in;
                    height: 11in;
                }

                /* allow colors to print */
                @media print {
                    body {
                        -webkit-print-color-adjust: exact; /* Chrome, Safari */
                        print-color-adjust: exact;        /* Firefox */
                    }
                }

                body {
                    margin: 0;
                    font-family: "HelveticaNeue-Light", "Helvetica Neue Light", 
                                 "Helvetica Neue", Helvetica, Arial, 
                                 "Lucida Grande", sans-serif;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                }
            </style>
                <script>
                    function imagesLoaded(retryCount = 0) {
                        try {
                            const images = document.body.querySelectorAll('img');
                            const imagesLoaded = Array.from(images).every(img => img.complete);
                            if (imagesLoaded) {
                                console.log('images loaded', { retryCount });
                                window.print();
                            } else {
                                setTimeout(() => imagesLoaded(retryCount + 1), 100);
                            }
                        } catch (error) {
                            console.error('error printing', error);
                            if (retryCount < 10) {
                                setTimeout(() => imagesLoaded(retryCount + 1), 100);
                            }
                        }
                    }
                    imagesLoaded();
                </script>
        </head>
        <body>
            ${innerHtml}
        </body>
        </html>
    `;


const printDiv = (ref) => {
    const printContent = ref.current;
    const win = window.open('', '', '');
    const html = generate(printContent.innerHTML);
    win.document.write(html);
};

const getChecklistId = () => {
    try {
        return localStorage.getItem(CACHE_KEY.JOB_SHEET_CHECKLIST);
    } catch (err) {
        return null;
    }
};

const getOptions = () => {
    try {
        const found = JSON.parse(localStorage.getItem(CACHE_KEY.JOB_SHEET_OPTIONS));
        return { ...DEFAULT_OPTIONS, ...found };
    } catch (err) {
        return DEFAULT_OPTIONS;
    }
};


const PrintJobSheetModal = ({ jobId, filter, colors, checklists = [], part = {}, hashId, template }) => {
    // react
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [colorBand, setColorBand] = useState([]);
    const [checklistId, setChecklistId] = useState(null);
    const [checklist, setChecklist] = useState({});
    const [options, setOptions] = useState(getOptions());
    const printRef = useRef();
    // mui
    const sx = useStyles();

    useEffect(() => {
        // checklist
        const id = getChecklistId();
        setChecklistId(id);
        const found = checklists.find((c) => c?.id == id);

        if (found) {
            const { json_data } = found;
            setChecklist(json_data);
        }
    }, [checklists]);

    const saveChecklistId = (id) => {
        localStorage.setItem(CACHE_KEY.JOB_SHEET_CHECKLIST, id);
        const { json_data } = checklists.find((c) => c?.id == id) || {};
        setChecklist(json_data);
        setChecklistId(id);
    };

    const saveOptions = (name, value) => {
        const newOptions = { ...options, [name]: value };
        localStorage.setItem(CACHE_KEY.JOB_SHEET_OPTIONS, JSON.stringify(newOptions));
        setOptions(newOptions);
    };

    return (
        <div className='PrintJobSheetModal'>
            <Modal open={open} onClose={() => setOpen(false)} className='PrintJobSheetModal' disableBackdropClick disableScrollLock>
                <Container align='center'>
                    <Card className={sx.card}>
                        <CardContent>
                            {/* header */}
                            <Typography variant='h5' className={sx.modalHeader}>Print Job {jobId} Sheet</Typography>
                            <Divider className={sx.divider} />


                            {loaded
                                ? (
                                    <div className='ModalFooter'>

                                        {/* checklist */}
                                        <Typography variant='subtitle1' style={{ fontWeight: '600', marginTop: '1rem', marginBottom: '-0.85rem' }}>Select Checklist</Typography>
                                        <Select placeholder='Select Checklists' style={{ width: '75%', border: '1px solid gray', borderRadius: '5px', marginTop: '1rem' }} onChange={(e) => saveChecklistId(e.target.value)} value={checklistId}>
                                            {checklists.map((c) => <MenuItem key={c?.id} value={c?.id}>{c?.name}</MenuItem>)}
                                        </Select>

                                        {/* color band */}
                                        <ColorBandSelector colors={colors} setColorBand={setColorBand} />

                                        {Object.keys(checklist).length > 0 && (
                                            <div>
                                                {/* options checkboxes */}
                                                <Typography variant='subtitle1' style={{ fontWeight: '600', marginTop: '1rem' }}>Print Options</Typography>
                                                <Grid container style={{ border: '1px solid gray', borderRadius: '5px', padding: '0.3rem' }}>
                                                    <Grid item xs={3}>
                                                        <Typography variant='subtitle1'>Drawings</Typography>
                                                        <Checkbox color='black' checked={!!options?.drawings} onChange={(e) => saveOptions('drawings', e.target.checked)} />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Typography variant='subtitle1'>Layouts</Typography>
                                                        <Checkbox color='black' checked={!!options?.layouts} onChange={(e) => saveOptions('layouts', e.target.checked)} />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Typography variant='subtitle1'>Parts</Typography>
                                                        <Checkbox color='black' checked={!!options?.parts} onChange={(e) => saveOptions('parts', e.target.checked)} />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Typography variant='subtitle1'>Non Parts</Typography>
                                                        <Checkbox color='black' checked={!!options?.nonParts} onChange={(e) => saveOptions('nonParts', e.target.checked)} />
                                                    </Grid>
                                                </Grid>

                                                {/* footer buttons */}
                                                <div style={{ marginTop: '2rem', width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', textAlign: 'center' }}>
                                                    <Button colorTheme={Colors.Orange} type={ButtonType.Dashboard} onClick={() => printDiv(printRef)} label={'Print'} />
                                                    <Button colorTheme={Colors.Red} type={ButtonType.Close} label={'Close '} onClick={() => setOpen(false)} />
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                )
                                : (
                                    <CircularProgress />
                                )
                            }

                            {/* print content */}
                            <JobSheet {...{ printRef, jobId, setLoaded, filter, colorBand, part, hashId, checklist, template, options }} />

                        </CardContent>
                    </Card>

                </Container>
            </Modal>

            <Button
                className={sx.openButton}
                colorTheme={Colors.Sage} type={ButtonType.Edit}
                label={'Print Job Sheet'}
                onClick={() => setOpen(true)}
            />
        </div >
    );
};

PrintJobSheetModal.propTypes = {
    jobId: PropTypes.string,
    filter: PropTypes.object,
    colors: PropTypes.array,
    checklists: PropTypes.array,
    part: PropTypes.object,
    hashId: PropTypes.string,
    template: PropTypes.object
};

export default PrintJobSheetModal;